import {
  Suspense,
  lazy,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  Outlet,
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router'
import { cn, Portal, Spinner, Tooltip } from 'tailwind-ui'
import { AnimatePresence, motion } from 'framer-motion'
import { SidebarProvider } from 'tailwind-v4/components/sidebar'
import { AvailableRoles } from '@/state/auth/auth.types'
import useSelectedOrganisation from '@/state/entities/organisations/hooks/useSelectedOrganisation'
import { setOrgId } from '@/state/global/global.actions'
import { useAppDispatch } from '@/state/helpers/useApp'
import { getFirst } from '@/utils/common'
import { usePusherSubscription } from '@/utils/pusher'
// import { customerApi } from './customer/:id/layout'
import { ScrollArea } from '@/common/scroll-area'
import Blurrer from '@/components/Pii/Blurrer'
import { useAlertContext } from '@/components/alerts'
import { InboxModalProvider } from '@/components/inbox/context/modal'
import { CreateReviewPageDialog } from '@/components/Reviews/create-review-page-dialog'
import { WhatsNewDialog } from '@/components/whats-new/whats-new-dialog'
import { useFullSubscription } from '@/hooks/useSubscription'
import useUser, { useUserRole } from '@/hooks/useUser'
import { UserInviteProvider } from '@/providers/user-invite.context'
import inboxApi from '@/state/inbox/inbox.slice'
import { useUserConfig } from '@/state/morpheus/user-config/helpers'
import { usePageViewLogger } from '@/utils/tracking'
import BubbleLoad from '@/ux/bubble-load'
import { useHttpLoader } from '@/utils/use-http-loader'
import { useSideNavContext } from '@/utils/router/sidenav-provider-optimized'
import { useFeatureFlags } from '@/hooks/use-feature-flags'
import { AccessProvider, VenueAccessBoundary } from '@/access'
import {
  NewSidebarShell,
  useNewNavCtx,
} from '@/components/nav-v4/new-sidebar-shell'
import { differenceInCalendarDays } from 'date-fns'
import { TrialBanner } from '@/components/billing/trial-banner'
import { SubscriptionRequiredNotice } from '@/components/billing/subscription-required-notice'
import { NewTopBar } from '@/components/nav-v4/new-top-bar'
import { MobileNav } from '@/components/nav-v4/mobile-nav'
import { orgNavPrimary, orgNavSecondary } from '@/components/nav-v4/config'
import { openSupportDock, SupportDock } from '@/components/support-dock'
import { oldOrgNavPrimary, oldOrgNavSecondary } from './old-nav-config'
import { useNavCtx } from './nav/use-nav-ctx'
import { OldTopBar } from './nav/old-top-bar'
import {
  KnowledgeBaseSidePanel,
  KNOWLEDGE_BASE_PANEL_RESERVED_WIDTH,
} from './knowledge-base/side-panel'
import {
  KnowledgeBasePanelProvider,
  useKnowledgeBasePanel,
} from '@/providers/knowledge-base-panel.context'

//import style from './app.module.css'

const showNavOn = [
  '/',
  '/venues',
  '/marketing',
  '/reviews',
  '/gifting',
  '/loyalty',
  '/reports',
  '/bookings',
  '/venues/:serial/booking',
  '/venues/:serial/loyalty',
  '/venues/:serial/bookings/menus/items',
  '/venues/:serial/bookings/menus/modifier-categories',
  // NOT a detail page, despite sitting where one would. The enquiry-detail
  // hide below is `/enquiries/:id`, and `:id` is a segment like any other, so
  // it swallowed `/enquiries/reports` and the reports page lost the nav.
  '/bookings/enquiries/reports',
  '/venues/:serial/bookings/enquiries/reports',
]

export const globalHideNavOn = [
  // Campaign editor (`/marketing/email/campaigns/:id`) keeps the
  // global nav now — the editor's own header sits underneath the
  // top bar and doesn't conflict. Templates editor still hides
  // because its surface is full-screen and the top bar would
  // crowd the canvas.
  '/marketing/email/templates/:id',
  '/venues/:serial/loyalty/:id/service/*',
  // '/venues/:serial/loyalty/:id/customers/:customer_id',
  '/venues/:serial/bookings/day-planner',
  '/venues/:serial/bookings/list',
  // Enquiry DETAIL only (the list keeps the nav — these patterns are exact, so
  // `/enquiries` doesn't match). A full-height takeover: its own nav rail on
  // desktop, its own back button at every width, and a bottom-docked message
  // composer that the floating mobile pill + fade landed straight on top of.
  '/venues/:serial/bookings/enquiries/:id',
  '/bookings/enquiries/:id',
  // Enquiries workspace (zion ENQUIRIES_WORKSPACE_ARD.md D17): a full-page app
  // with its own rail and back button. Staff only via the route capability, so
  // an unconditional hide is safe.
  '/bookings/enquiries-workspace',
  '/bookings/enquiries-workspace/*',
  '/venues/:serial/bookings/enquiries-workspace',
  '/venues/:serial/bookings/enquiries-workspace/*',
  '/venues/:serial/bookings/floor-plans',
  '/venues/:serial/bookings/reporting/*',
  '/venues/:serial/bookings/fast-view',
  '/venues/:serial/terminal',
  '/venues/:serial/terminal/*',
  '/workflows/:id',
  // Per-section hides for the legacy bookings settings pages — the new
  // index pages (e.g. `/settings/areas`) deliberately keep the sidebar.
  // As more sub-pages get rebuilt we drop entries from this list.
  // booking-types / special-dates / events / upgrades now keep the sidebar
  // (their layouts are bare context providers, no competing nav rail).
  '/venues/:serial/bookings/settings/automations',
  '/venues/:serial/bookings/settings/automations/*',
  '/venues/:serial/bookings/settings/concierge',
  '/venues/:serial/bookings/settings/concierge/*',
  '/venues/:serial/bookings/settings/messages',
  '/venues/:serial/bookings/settings/messages/*',
  // '/reviews/ai-settings',
  '/account/apps/square/:squareId/setup',
  '/chat',
  '/chat/*',

  // 'reviews/ai-settings'
]

/**
 * THE ONE RULE FOR WHETHER THE GLOBAL NAV IS HIDDEN.
 *
 * It was written out twice - here and in `tabbed-nav-optimized` - and only
 * this copy honoured `showNavOn`. So an exception added to the show list
 * worked in one place and not the other, which is how `/enquiries/reports`
 * came to be matched by the enquiry-detail pattern and lose its nav.
 */
export function hidesGlobalNav(pathname: string): boolean {
  const matches = (patterns: readonly string[]) =>
    patterns.some((pattern) => matchPath(`/:org_id${pattern}`, pathname))
  return matches(globalHideNavOn) && !matches(showNavOn)
}

/**
 * Routes where the global nav hides only when the user is on the legacy
 * UI. Used for pages where new + legacy share the same URL and the legacy
 * chrome wraps the page with its own header/rail, so adding the global
 * sidebar on top would cause two competing nav surfaces.
 *
 * Empty now: the legacy bookings settings shell no longer renders its own
 * inner rail (the global sidebar drill-in replaced it), so main settings
 * keeps the global nav like every other settings page.
 */
export const legacyChromeHidesNavOn: string[] = []

/**
 * Routes that stay reachable even when the org has no subscription record
 * (see `hasNoSubscription` below) — a locked-out org still needs to see its
 * billing status, and its members still need their own account pages.
 */
export const subscriptionLockoutExemptOn = [
  '/account/organisation/billing',
  '/account/profile',
  '/account/notifications',
  '/account/organisation/team',
  '/whats-new',
  '/knowledge-base',
]

function isChatRoute(path: string): boolean {
  return path.split('/').includes('chat')
}

function getErrorStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status?: unknown }).status
    return typeof status === 'number' ? status : undefined
  }
  return undefined
}

/**
 * Reserves room on the right for the docked Knowledge Base panel
 * (side-panel.tsx) by reading `isOpen` from `useKnowledgeBasePanel()` —
 * NOT the URL. A component of its own because a component can't consume
 * the context it renders `<KnowledgeBasePanelProvider>` for itself; this
 * one sits BELOW that provider in the tree so the read is real.
 */
function PanelPaddedColumn({
  className,
  children,
}: {
  className: string
  children: ReactNode
}) {
  const { isOpen } = useKnowledgeBasePanel()
  return (
    <div
      className={cn(
        className,
        'transition-[padding-right] duration-150 ease-in-out'
      )}
      style={{
        paddingRight: isOpen ? KNOWLEDGE_BASE_PANEL_RESERVED_WIDTH : undefined,
      }}
    >
      {children}
    </div>
  )
}

function DashLayout() {
  const { pathname } = useLocation()
  const prevPathRef = useRef(pathname)
  console.log('pathname', pathname)

  // Force full page reload when crossing between chat (COEP) and non-chat (no COEP) zones
  // This ensures the browser gets the correct COEP headers for WebContainer/Unlayer
  useEffect(() => {
    const prev = prevPathRef.current
    prevPathRef.current = pathname

    if (prev !== pathname && isChatRoute(prev) !== isChatRoute(pathname)) {
      window.location.href = pathname
    }
  }, [pathname])
  // const shouldHaveApp = useShouldHaveApp()
  const organisation = useSelectedOrganisation()
  const user = useUser()
  const role = useUserRole()
  const { newUi, subscriptionLockoutOverride } = useFeatureFlags()
  // Both nav contexts are built unconditionally (hooks rule); only one feeds
  // the rendered sidebar. New UI uses the live ctx (resolved venue); old UI
  // uses its own ctx (venue:null + schemeId) so the shared NewSidebarShell
  // renders old-UI hrefs/structure.
  const newNavCtx = useNewNavCtx()
  const oldNavCtx = useNavCtx()
  const push = useNavigate()
  const dispatch = useAppDispatch()
  const {
    data: subscription,
    error: subscriptionError,
    isLoading: isSubscriptionLoading,
  } = useFullSubscription()
  const [alerts, setAlerts] = useAlertContext()

  const [isOrgLoadingSlow, setIsOrgLoadingSlow] = useState(false)

  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    if (!organisation) {
      // Start timing when organization is null
      timeoutId = setTimeout(() => {
        setIsOrgLoadingSlow(true)
      }, 5000) // 5 seconds
    }

    return () => {
      // Clean up timeout if organization loads before 5 seconds
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [organisation])

  usePageViewLogger()
  const { search } = useLocation()
  // Simple media query hook replacement
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)')
    const handleChange = () => {
      setIsMobile(mediaQuery.matches)
    }
    handleChange() // Set initial value
    mediaQuery.addEventListener('change', handleChange)
    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])
  const { org_id } = useParams<{ org_id: string }>()

  const showMobileWhenDrawerOpen = useMemo(() => {
    const urlParams = new URLSearchParams(search)
    const isDrawerOpen = urlParams.get('main_drawer') === 'open'
    if (pathname === `/${org_id}`) return false
    return !(isMobile && isDrawerOpen)
  }, [search, org_id])

  const isLockoutExempt = useMemo(
    () =>
      subscriptionLockoutExemptOn.some((item) =>
        matchPath(`/:org_id${item}`, pathname)
      ),
    [pathname]
  )

  const hiddenOn = useMemo(() => {
    const urlParams = new URLSearchParams(search)

    // Pages that share a URL between legacy and new UI: legacy chrome
    // brings its own nav rail, so we hide the global nav only when the
    // user is on legacy (i.e. flag is off). On new UI the global
    // sidebar stays visible.
    const legacyChromeHide =
      !newUi.enabled &&
      legacyChromeHidesNavOn.some((item: any) =>
        matchPath(`/:org_id${item}`, pathname)
      )

    return hidesGlobalNav(pathname) || legacyChromeHide
  }, [pathname, search, newUi.enabled])

  useEffect(() => {
    if (!org_id) return
    dispatch(setOrgId(org_id))
  }, [org_id, dispatch])

  useEffect(() => {
    if (!role) return
    if (pathname.includes('bookings')) return
    if (role.name !== AvailableRoles.Booking) return
    if (!organisation?.locations) return
    const location = getFirst(organisation.locations)
    // Only redirect from the root org page, not from other pages
    const isRootOrgPage =
      matchPath(':org_id/', pathname) && pathname.split('/').length === 2
    if (location && isRootOrgPage) {
      push(`venues/${location.serial}/bookings`)
    }
  }, [role?.name, pathname])

  usePusherSubscription<{ open: number; closed: number }>(
    'inbox.thread.unread_count.updated',
    (data: any) => {
      console.log('pusher_inbox_thread_unread_count_updated', data)
      dispatch(
        inboxApi.util.invalidateTags([
          { type: 'counts', id: 'LIST' },
          { type: 'counts' }, // Invalidate all counts
          { type: 'threads', id: 'LIST' },
          { type: 'threads' },
        ])
      )
    }
  )

  const trialDaysRemaining = useMemo((): number | null => {
    if (!subscription?.subscription.is_trial) return null
    const endsAt = subscription.subscription.trial_ends_at
    if (!endsAt) return null
    return Math.max(0, differenceInCalendarDays(new Date(endsAt), new Date()))
  }, [subscription])

  const [trialBannerDismissed, setTrialBannerDismissed] = useState(false)

  useEffect(() => {
    if (!subscription) return
    if (subscription?.needs_contact_upgrade > 0) {
      setAlerts({
        itemKey: 'contacts',
        type: 'warning',
        title: `Additional ${subscription?.needs_contact_upgrade} contacts required, upgrade to continue using marketing, wifi and inbox`,
        onClick: () => openSupportDock(),
      })
    }
    if (subscription?.needs_venue_upgrade > 0) {
      setAlerts({
        itemKey: 'venue',
        title: `Additional ${subscription?.needs_venue_upgrade} venues required, upgrade to continue using wifi, reviews and marketing`,

        type: 'warning',
        onClick: () => openSupportDock(),
      })
    }
  }, [subscription, setAlerts])

  /**
   * Two causes, one lockout UI: no subscription record exists at all (the
   * public lookup 404s — a legacy org, or one deleted by an admin), or a
   * subscription record exists but is inactive (`active: false` — genuinely
   * suspended for non-payment). Either way the whole app is blocked with the
   * full-page `SubscriptionRequiredNotice` (except `subscriptionLockoutExemptOn`).
   * See the `<Outlet/>` swap below. Deliberately reads `!subscription` as
   * active so this doesn't double-fire while the no-subscription case is
   * still loading/erroring — that org was never suspended for non-payment,
   * it never had a plan.
   */
  const hasNoSubscription =
    !isSubscriptionLoading && getErrorStatus(subscriptionError) === 404

  const isSubscriptionActive = useMemo((): boolean => {
    if (!subscription) {
      return true
    }

    return subscription.active
  }, [subscription])

  // Lets a stampede staff member dismiss the lockout for this session (see
  // `subscriptionLockoutOverride` in use-feature-flags.ts) — never available
  // to customers.
  const [subscriptionLockoutDismissed, setSubscriptionLockoutDismissed] =
    useState(false)
  const showSubscriptionLockout =
    (hasNoSubscription || !isSubscriptionActive) &&
    !(subscriptionLockoutOverride.enabled && subscriptionLockoutDismissed)

  const config = useUserConfig()

  const isHttpLoading = useHttpLoader()
  const [
    {
      isMainNavCollapsed,
      isNavCollapsed,
      hasSecondaryNav,
      isSecondaryNavCollapsed,
    },
  ] = useSideNavContext()

  // Determine if header should be visible on mobile
  const shouldShowHeader =
    isMobile && isNavCollapsed && !(hasSecondaryNav && !isSecondaryNavCollapsed)

  // Lock body scroll on mobile to prevent content escaping and support floating nav
  useEffect(() => {
    if (!isMobile) return

    // Lock body scroll
    const originalStyle = window.getComputedStyle(document.body).overflow
    const originalPosition = window.getComputedStyle(document.body).position
    const originalTop = document.body.style.top
    const originalHeight = document.body.style.height
    const scrollY = window.scrollY

    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.width = '100%'
    // Use visual viewport height for PWA if available, otherwise use innerHeight
    const vh = window.visualViewport?.height || window.innerHeight
    document.body.style.height = `${vh}px`

    // Prevent touchmove on body (but allow it on scrollable content and interactive elements)
    const preventBodyScroll = (e: TouchEvent) => {
      const target = e.target as HTMLElement
      // Allow scrolling on elements that should scroll
      if (
        target.closest('[data-scroll-container]') ||
        target.closest('.overflow-auto') ||
        target.closest('.overflow-y-auto') ||
        target.closest('.overflow-scroll') ||
        // Allow on clickable/interactive elements to prevent interfering with clicks
        target.closest('a') ||
        target.closest('button') ||
        target.closest('[role="button"]') ||
        target.closest('[onclick]')
      ) {
        return
      }
      e.preventDefault()
    }

    document.body.addEventListener('touchmove', preventBodyScroll, {
      passive: false,
    })

    return () => {
      // Restore original styles
      document.body.style.overflow = originalStyle
      document.body.style.position = originalPosition
      document.body.style.top = originalTop
      document.body.style.height = originalHeight
      document.body.style.left = ''
      document.body.style.right = ''
      document.body.style.width = ''
      document.body.removeEventListener('touchmove', preventBodyScroll)
      // Restore scroll position
      window.scrollTo(0, scrollY)
    }
  }, [isMobile])

  if (!organisation || !user) {
    return (
      <div className="h-full w-full flex items-center justify-center absolute inset-0">
        <BubbleLoad
          text={
            isOrgLoadingSlow
              ? 'This is taking longer than usual, sit tight...'
              : 'Loading account...'
          }
        />
      </div>
    )
  }

  return (
    /**
     * Everything org-scoped sits inside `AccessProvider`, so any page or
     * component below can ask `useAccess().can(…)` without wiring up its own
     * role / staff / plan / capability queries.
     *
     * Mounted here rather than in `RootLayout` because the subject needs
     * `org_id` in the URL: the org role, subscription, Square and data-provider
     * queries are all org-scoped and would skip above this route.
     *
     * The two nav context builders above (`useNewNavCtx` / `useNavCtx`) resolve
     * their own subject rather than reading this one, since they run before the
     * provider mounts. That is safe because resolution is a single shared
     * function over shared RTK cache entries — the thing that must not be
     * duplicated is the POLICY, and there is exactly one of those.
     */
    <AccessProvider>
      <UserInviteProvider key="UserInviteProvider">
        <Portal className="fixed left-0.5 top-0.5 z-50  flex items-center justify-center">
          <motion.div
            className="flex items-center justify-center bg-green-500 rounded-full p-0 size-4"
            initial="idle"
            exit="idle"
            animate={isHttpLoading ? 'loading' : 'idle'}
            variants={{
              idle: { scale: 0 },
              loading: { scale: 1 },
            }}
            transition={{ duration: 0.5 }}
          >
            <Spinner
              size="xs"
              brandKit={false}
              className="text-green-500 fill-green-900/50"
            />
          </motion.div>
        </Portal>
        <KnowledgeBasePanelProvider>
          <InboxModalProvider>
            <CreateReviewPageDialog />
            {/* Fires once, on the org home, for the people who could act on it.
              Inside AccessProvider so it can read the subject it gates on. */}
            <WhatsNewDialog />
            {newUi.enabled ? (
              <div className="new-ui-theme h-full w-full bg-[var(--background)] text-[var(--foreground)]">
                <SidebarProvider>
                  {!hiddenOn && (
                    <NewSidebarShell
                      primary={orgNavPrimary}
                      secondary={orgNavSecondary}
                      ctx={newNavCtx}
                    />
                  )}
                  <PanelPaddedColumn className="flex flex-1 flex-col h-dvh overflow-hidden">
                    {!hiddenOn && <NewTopBar />}
                    {!hiddenOn &&
                      trialDaysRemaining !== null &&
                      !trialBannerDismissed && (
                        <TrialBanner
                          daysRemaining={trialDaysRemaining}
                          tone={trialDaysRemaining <= 7 ? 'warning' : 'info'}
                          onContactUs={() => openSupportDock()}
                          onDismiss={() => setTrialBannerDismissed(true)}
                          className="mx-4 mt-4"
                        />
                      )}
                    {/* Full page height on mobile — content scrolls the whole
                    way down under the floating Menu pill, no reserved gap. */}
                    <div
                      data-scroll-container
                      className="relative flex-1 overflow-auto flex flex-col bg-[var(--background)] scroll-py-4"
                    >
                      {showSubscriptionLockout && !isLockoutExempt ? (
                        <SubscriptionRequiredNotice
                          canOverride={subscriptionLockoutOverride.enabled}
                          onOverride={() =>
                            setSubscriptionLockoutDismissed(true)
                          }
                        />
                      ) : (
                        <VenueAccessBoundary>
                          <Outlet />
                        </VenueAccessBoundary>
                      )}
                    </div>
                  </PanelPaddedColumn>
                </SidebarProvider>
              </div>
            ) : (
              <SidebarProvider className="h-full w-full">
                {/* Same renderer as the new UI, fed old-UI nav data + ctx. Only the
                sidebar is wrapped in `new-ui-theme` (via display:contents so it
                stays a direct flex child and adds no box) — custom properties
                inherit to the v4 Sidebar while the page content below keeps its
                old-UI styling, unthemed. */}
                {!hiddenOn && (
                  <div
                    className="new-ui-theme old-ui-nav"
                    style={{ display: 'contents' }}
                  >
                    <NewSidebarShell
                      primary={oldOrgNavPrimary}
                      secondary={oldOrgNavSecondary}
                      ctx={oldNavCtx}
                    />
                  </div>
                )}
                {/* Content column: top bar (breadcrumb + venue switcher) above a
                dvh-bounded scroll area so inner scrollers (e.g. day-planner
                timeline) can bound and scroll. */}
                <PanelPaddedColumn className="flex h-dvh min-w-0 flex-1 flex-col overflow-hidden">
                  {!hiddenOn && <OldTopBar />}
                  {!hiddenOn &&
                    trialDaysRemaining !== null &&
                    !trialBannerDismissed && (
                      <TrialBanner
                        daysRemaining={trialDaysRemaining}
                        tone={trialDaysRemaining <= 7 ? 'warning' : 'info'}
                        onContactUs={() => openSupportDock()}
                        onDismiss={() => setTrialBannerDismissed(true)}
                        className="mx-4 mt-4"
                      />
                    )}
                  {/* Paint the token canvas even in old-mode so migrated pages
                  (which resolve `--background`) sit on the neutral off-white
                  instead of raw browser white. `.new-ui-theme` only *defines*
                  the tokens (no base restyle), so old-ui pages are unaffected;
                  dark mode resolves via `.dark .new-ui-theme`. */}
                  <div
                    data-scroll-container
                    className={cn(
                      'new-ui-theme relative flex-1 overflow-auto flex flex-col scroll-py-4 bg-[var(--background)]',
                      {
                        'rounded-b-lg': !hiddenOn && !isMobile,
                      }
                    )}
                  >
                    {showSubscriptionLockout && !isLockoutExempt ? (
                      <SubscriptionRequiredNotice
                        canOverride={subscriptionLockoutOverride.enabled}
                        onOverride={() => setSubscriptionLockoutDismissed(true)}
                      />
                    ) : (
                      <VenueAccessBoundary>
                        <Outlet />
                      </VenueAccessBoundary>
                    )}
                  </div>
                </PanelPaddedColumn>
                {!hiddenOn && (
                  <MobileNav
                    primary={oldOrgNavPrimary}
                    secondary={oldOrgNavSecondary}
                    ctx={oldNavCtx}
                    themeClassName="new-ui-theme old-ui-nav"
                  />
                )}
              </SidebarProvider>
            )}

            <KnowledgeBaseSidePanel />
            {/* The support layer. Mounted HERE, as a sibling of the outlet,
                so the agent can change the page underneath it and the
                conversation carries on. Renders nothing until somebody opens
                it (cmd+/ or openSupportDock()). */}
            <SupportDock />
            <Blurrer />
          </InboxModalProvider>
        </KnowledgeBasePanelProvider>
      </UserInviteProvider>
    </AccessProvider>
  )
}

export default DashLayout
